import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { RootState } from "@platformx/authoring-state";
import { NoResults } from "@platformx/shared/static-assets";
import {
  AUTH_INFO,
  NoContentFound,
  PlatFormXDateTimeFormat,
  buildImageObjectWithColor,
  iconsList,
} from "@platformx/utilities";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import { getIconWithTitle } from "../ContentTypeCard/helper";
import useGalleryStyle from "./ContentGallery.styles";
import { isSameGalleryItem } from "./utils/matchGalleryItem";

const GalleryListItems = ({
  galleryObj,
  loading,
  isLazyLoad,
  multi_select,
  fetchMoreData,
  multiSelectedHandle,
  singleSelectedHandle,
  single_select,
  selectedmultiSelect,
}) => {
  const { routes = [] } = useSelector((state: RootState) => state?.routes);
  const theme = useTheme();
  const classes = useGalleryStyle();
  const isSelectable = Boolean(single_select || multi_select);
  const secondaryArgs = {
    prelemBaseEndpoint: {
      bucketUrl: AUTH_INFO.gcpUri || "",
      bucketName: AUTH_INFO.gcpBucketName || "",
    },
  };
  const isError = false;

  const handleSelectedItem = (item, index) => {
    if (multi_select) {
      multiSelectedHandle(item, index);
    } else if (single_select) {
      singleSelectedHandle(item, index);
    }
  };

  const handleRowKeyDown = (event, item, index) => {
    if (!isSelectable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelectedItem(item, index);
    }
  };

  const getSelectionOrder = (item) =>
    selectedmultiSelect.findIndex((selectedItem) => isSameGalleryItem(selectedItem, item)) + 1;

  const getContentTypeLabel = (contentType = "") => {
    if (["VOD", "Quiz", "Poll"].includes(contentType)) return contentType;
    return contentType?.replace(/([A-Z])/g, " $1").trim();
  };

  const handleImageError = (event, fallback) => {
    if (fallback) event.target.src = fallback;
  };

  const getTypeIconSrc = ({ contentType = "", listIcon = "", icon = "", menuIcon = "" }) =>
    iconsList?.[contentType] ||
    iconsList?.[contentType?.toLowerCase()] ||
    listIcon ||
    icon ||
    menuIcon;

  const getRow = (item, index) => {
    const { ContentType = "", Title = "", Author = "" } = item || {};
    const {
      icon = "",
      menuIcon = "",
      listIcon = "",
      fallbackIcon = "",
    } = getIconWithTitle(routes, ContentType);
    const typeIconSrc = getTypeIconSrc({ contentType: ContentType, listIcon, icon, menuIcon });
    const { color, imageUrl } = buildImageObjectWithColor(item, secondaryArgs);
    const isChecked = Boolean(item?.is_checked);
    const displayDate = item?.PublishedDate || item?.lastModifiedDate || "";

    return (
      <Box
        key={index}
        className={`${classes.galleryListRow} ${isChecked ? "selected" : ""}`}
        data-testid={`gallery-list-row-${index}`}
        onClick={() => handleSelectedItem(item, index)}
        onKeyDown={(event) => handleRowKeyDown(event, item, index)}
        role={isSelectable ? "button" : undefined}
        tabIndex={isSelectable ? 0 : undefined}
        aria-pressed={isSelectable ? isChecked : undefined}
        aria-label={Title || undefined}
        sx={{ cursor: isSelectable ? "pointer" : "default" }}>
        {isSelectable && (
          <Box className='galleryListCheck' aria-hidden='true'>
            {isChecked ? (
              <CheckBoxIcon sx={{ color: "#1E64ED", fontSize: "24px" }} />
            ) : (
              <CheckBoxOutlineBlankIcon sx={{ color: "#8991A5", fontSize: "24px" }} />
            )}
          </Box>
        )}

        <Box
          className='galleryListThumb'
          sx={{ backgroundColor: color || theme?.palette?.customColor?.BGLIGHT }}>
          {imageUrl ? <img src={imageUrl} alt='' aria-hidden='true' /> : null}
        </Box>

        <Box className='galleryListMain'>
          <Typography variant='p2medium' className='galleryListTitle'>
            {Title}
          </Typography>
          <Box className='galleryListMetaInline'>
            {Author ? (
              <Typography variant='p4regular' className='galleryListMetaText'>
                {Author}
              </Typography>
            ) : null}
            {displayDate ? (
              <Typography variant='p4regular' className='galleryListMetaText'>
                {PlatFormXDateTimeFormat(displayDate)}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {typeIconSrc ? (
          <>
            <Box className='galleryListPipe typePipe' aria-hidden='true' />
            <Box className='galleryListTypeIcon' data-testid={`gallery-list-type-${index}`}>
              <img
                src={typeIconSrc}
                alt={getContentTypeLabel(ContentType)}
                title={getContentTypeLabel(ContentType)}
                onError={(event) => handleImageError(event, fallbackIcon)}
              />
            </Box>
            <Box className='galleryListPipe metaPipe' aria-hidden='true' />
          </>
        ) : null}

        <Box className='galleryListMeta'>
          {Author ? (
            <Typography
              variant='p4regular'
              className='galleryListMetaText galleryListAuthor'
              title={Author}>
              {Author}
            </Typography>
          ) : null}
          {displayDate ? (
            <Typography variant='p4regular' className='galleryListMetaText'>
              {PlatFormXDateTimeFormat(displayDate)}
            </Typography>
          ) : null}
        </Box>

        {multi_select ? (
          <Box className='galleryListOrderSlot'>
            {isChecked ? (
              <Chip
                size='small'
                className='galleryListOrder'
                label={getSelectionOrder(item)}
                color='info'
              />
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <div>
      {isError ? (
        <Box
          sx={{
            marginTop: "200px",
            marginBottom: "100px",
            textAlign: "center",
          }}>
          <img src={NoResults} alt='' />
          <Typography variant='h3semibold'>Failed to fetch results</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: "calc(100vh - 160px)",
            overflowY: "scroll",
          }}
          id='scrollablegallerydiv'>
          {galleryObj && galleryObj?.length === 0 && !loading ? (
            <Box
              sx={{
                width: "100%",
                height: "75%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <NoContentFound />
            </Box>
          ) : (
            <Box className={classes.galleryListWrapper}>
              <InfiniteScroll
                dataLength={galleryObj.length}
                next={fetchMoreData}
                hasMore={isLazyLoad}
                loader={null}
                scrollableTarget='scrollablegallerydiv'>
                {galleryObj?.map((item, index) => getRow(item, index))}
              </InfiniteScroll>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default GalleryListItems;
